import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MemoryIcon from "@mui/icons-material/Memory";
import SettingsIcon from "@mui/icons-material/Settings";

import { useDispatch, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpByproductService from "@plannel/services/fp/fp-byproduct-service";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import FlowChart from "@plannel/pages/factory-plan/common/component/FlowChart";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import InventoryPopup from "@plannel/pages/factory-plan/common/popup/InventoryPopup";
import RoutePopup from "@plannel/pages/factory-plan/common/popup/RoutePopup";
import { byproductFloToFlow } from "@plannel/pages/factory-plan/common/flowchart";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import SplitterContainer from "@plannel/components/SplitterContainer";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// 백엔드 ByproductTreeDto.type — 공정(부모) / 주품목(공정이 생산하는 품목, 읽기전용) / 부산물(편집 대상).
const ROW_TYPE = { ROUTE: "route", MAIN: "mainProduct", BYPRODUCT: "byproduct" };

// 이 화면의 고정 조회조건 2개(§10) — old SearchOptsGroup 카탈로그(routeCdNm/inventoryCdNm)와 동일.
const SEARCH_FIELD_LABELS = { routeCdNm: "FP_ROUTE", inventoryCdNm: "FP_INVENTORY" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

// old validRules required 2개(routeCd/inventoryCd). old는 onValidateColumn에서 **부산물 행에만**
// 이 검사를 적용했으므로(mainProduct/route 행은 통과) 검사 결과도 부산물 행으로 걸러 쓴다(§9-2).
const REQUIRED_FIELDS = [
  { field: "routeCd", label: "FP_ROUTE_CD" },
  { field: "inventoryCd", label: "FP_INVENTORY_CD" }
];

// 트리 행 종류별 아이콘 — old setTreeOptions의 iconImages ["gear_24.png"(공정), "cpu.png"(그 하위)]
// + iconCallback(level === 1 ? gear : cpu). MUI 아이콘 매핑은 master/Bom.js·report/OrderTracking.js와 동일.
const TreeItemIcon = ({ type }) => (
  type === ROW_TYPE.ROUTE
    ? <SettingsIcon fontSize="small" sx={{ color: "text.secondary" }} />
    : <MemoryIcon fontSize="small" sx={{ color: "text.secondary" }} />
);

// 저장/삭제 payload에 실리면 안 되는 화면 전용 필드(트리 경로/행 종류 표시).
const stripClientFields = ({ path, type, isNewRoute, rowIndex, ...rest }) => rest;

/**
 * 백엔드 `getByproductTree` 응답(공정 1건 = 부모 1행 + `rows`에 주품목/부산물 자식)을
 * AG Grid Tree Data(`getDataPath`)용 경로 배열로 평탄화한다.
 * 경로 세그먼트는 화면에 노출되지 않는다(표시값은 autoGroupColumnDef의 valueGetter가 만든다).
 */
const flattenByproductTrees = (trees) => {
  const rows = [];

  (trees ?? []).forEach((tree, treeIndex) => {
    const { rows: children, ...route } = tree;
    const routeKey = `${route.routeCd ?? ""}__${treeIndex}`;

    rows.push({ ...route, type: ROW_TYPE.ROUTE, path: [routeKey] });
    (children ?? []).forEach((child, childIndex) => {
      const { rows: unusedRows, ...row } = child;
      rows.push({ ...row, path: [routeKey, `${row.type}__${childIndex}`] });
    });
  });

  return rows;
};

/**
 * 연산품(Byproduct) — 공정별로 [주품목 + 부산물 N건]을 트리 그리드로 편집하고, 선택한 공정의
 * 생산/소비/부산물 흐름을 하단 다이어그램으로 보여주는 화면.
 * old `view/factoryplan/master/byproduct/Byproduct.jsx` + `ByproductDiagram.jsx` 이관.
 *
 * 표준 Master CRUD(Route.js)와 다른 점은 **편집 가능한 트리 그리드**라는 것뿐이다
 * (fp-master-screen-guide.md §15). 다이어그램은 FLO 파일럿의 공용 엔진
 * (`common/flowchart.js` + `common/component/FlowChart.js`/`FlowNode.js`)을 그대로 재사용한다(§14).
 */
const Byproduct = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [searchFields, setSearchFields] = useState({
    routeCdNm: reduxViewState?.routeCdNm ?? "",
    inventoryCdNm: reduxViewState?.inventoryCdNm ?? ""
  });

  const [rows, setRows] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);
  const [routePopup, setRoutePopup] = useState({ open: false, node: null });
  const [inventoryPopup, setInventoryPopup] = useState({ open: false, node: null });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  // 신규 행 경로 채번 — 공정 코드는 팝업에서 나중에 채워지므로(빈 값) 트리 경로 키로 쓸 수 없다.
  const newRowSeqRef = useRef(0);

  // 생산 값 타입(FP_OUTPUT_VAL_TP_CD) 콤보 — §8 공통코드 콤보 공통 훅(common/grid.js).
  const outputValTpValuesRef = useCommonCodeValues("FP_OUTPUT_VAL_TP_CD");

  // 조회 토큰 가드(§0-8) — 조회가 2단(트리 목록 / 공정 다이어그램)이라 가드도 2개.
  const latestReqRef = useRef(0);
  const latestDiagramReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title, viewName }),
    treeData: true,
    getDataPath: (data) => data.path,
    // old loadData의 gridView.expandAll() — 조회 후 전체 전개.
    groupDefaultExpanded: -1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // 부산물 행만 편집 가능 — old setGridOptions의 styleCallback(type === 'byproduct'일 때만 editable).
  const editableWhenByproduct = (params) => params.data?.type === ROW_TYPE.BYPRODUCT;

  // 조회조건과 대조 — 서버 makeWhereClause(코드/명칭 부분일치, 대소문자 무시)와 동일 규칙.
  const matchesSearch = (data) => {
    const { routeCdNm, inventoryCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    if (routeCdNm && !(hit(data.routeCd, routeCdNm) || hit(data.routeNm, routeCdNm))) return false;
    if (inventoryCdNm && !(hit(data.inventoryCd, inventoryCdNm) || hit(data.inventoryNm, inventoryCdNm))) return false;
    return true;
  };

  // §6-1 케이스3(클라이언트 즉시필터). old는 서버가 **부산물 행**을 걸러낸 뒤 공정 단위로 묶어
  // 트리를 만들었으므로, 판정 대상도 부산물 행이다.
  //  - 부산물 행: 자기 값으로 판정(미저장 신규 행은 항상 통과 — §6-1)
  //  - 공정 행: 항상 false. 통과한 자식이 있으면 AG Grid Tree Data가 조상 행을 자동으로 남긴다.
  //    (true를 주면 반대로 "부모가 통과하면 자식 전부 포함"이 되어 조회조건이 무력화된다.)
  //  - 주품목 행: 형제 부산물 중 하나라도 통과하면 같이 남긴다(old도 트리에 항상 포함시켰다).
  const doesExternalFilterPass = (node) => {
    const data = node.data;
    if (!data) return true;

    if (data.type === ROW_TYPE.BYPRODUCT) return !data.id || matchesSearch(data);
    if (data.type === ROW_TYPE.ROUTE) return false;

    const siblings = node.parent?.childrenAfterGroup ?? [];
    return siblings.some((sibling) => {
      const siblingData = sibling.data;
      return siblingData?.type === ROW_TYPE.BYPRODUCT && (!siblingData.id || matchesSearch(siblingData));
    });
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some((value) => !!value);

  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  // TODO(MIG-T2): [byproduct/Byproduct] 조회조건 Enter 시 미저장 변경 경고(MSG_5142) 미이관.
  //   old: onParamKeyPress에서 getViewIsUpdated(viewId)면 "저장하지 않은 변경이 있다" 확인 후 재조회.
  //   사유: FP 전 화면 공통 유보 패턴(route/resource/stock/wip/simulationstep과 동일 — 화면별로 따로 고치지 않는다).
  const onSearchFieldChange = (tpCd, value) => setSearchFields((prev) => ({ ...prev, [tpCd]: value }));

  // 딥링크 수신(§13-2) — FLO/BOM 다이어그램 등에서 공정 코드를 들고 이동해 올 수 있다.
  // 케이스3(클라 외부 필터)이라 setState만으로 반영된다(§13-3).
  const incomingNav = useSelector((state) => state.historyState.history);
  useEffect(() => {
    if (incomingNav !== viewName) return;
    const freshState = reduxUtil.getViewState(viewName);
    if (!freshState) return;

    if (freshState.planScope) setPlanScope(freshState.planScope);
    setSearchFields((prev) => ({
      routeCdNm: freshState.routeCdNm ?? prev.routeCdNm,
      inventoryCdNm: freshState.inventoryCdNm ?? prev.inventoryCdNm
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingNav]);

  // old byproductGridItems 18개 중 routeCd는 트리 표시 컬럼(autoGroupColumnDef)으로 옮겨간다 —
  // 나머지 17개(숨김 5개 포함)가 여기 있고, 합계는 old와 동일하게 18개다.
  // TODO(MIG-T2): [byproduct/Byproduct] 컬럼 자동 필터(autoFilter) 미이관.
  //   old: routeCd/routeNm/inventoryCd/inventoryNm/itemCd/itemNm/itemClassCd/grade/outputVal 컬럼에
  //        `autoFilter: true`(헤더 드롭다운 값 목록 필터).
  //   사유: 컬럼 필터는 Tier2 표준 유보 항목(saas-fp/docs/fp-frontend-migration/CLAUDE.md) — 조회조건(공정/인벤토리 클라 필터)으로 1차 대체.
  const columnDefs = [
    { headerName: "FP_ROUTE_NM", field: "routeNm", colId: "routeNm", hide: true, type: ["nonEditableColumn"] },
    // old columnLayout의 접기형 그룹 itemDetailGroup(expandable, 기본 접힘).
    // 접힘 대표 = 인벤토리 코드/품목 코드(old groupShowMode "always" 2개), 나머지는 펼칠 때만.
    {
      headerName: "FP_ITEM_DETAIL", groupId: "itemDetail", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_INVENTORY_CD", field: "inventoryCd", colId: "inventoryCd", width: 180, editable: false,
          // 인벤토리 조회 버튼은 부산물 행에만 노출(old buttonVisibleCallback: type === 'byproduct').
          cellRendererSelector: (params) => (
            params.data?.type === ROW_TYPE.BYPRODUCT
              ? { component: CodePickerRenderer, params: { onOpenPopup: (node) => setInventoryPopup({ open: true, node }) } }
              : undefined
          ) },
        { headerName: "FP_INVENTORY_NAME", field: "inventoryNm", colId: "inventoryNm", width: 240, type: ["nonEditableColumn"], columnGroupShow: "open" },
        { headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 180, type: ["nonEditableColumn"] },
        { headerName: "FP_ITEM_NM", field: "itemNm", colId: "itemNm", width: 240, type: ["nonEditableColumn"], columnGroupShow: "open" },
        // old lookupDisplay + editable:false → §8-2 valueFormatter(라벨 표시, 키 없으면 코드).
        { headerName: "FP_ITEM_CLASS_CD", field: "itemClassCd", colId: "itemClassCd", width: 110,
          type: ["nonEditableColumn", "centerAligned"], columnGroupShow: "open",
          valueFormatter: (params) => {
            const key = `FP_ITEM_CLASS_CD_${params.value}`;
            const label = t(key);
            return label !== key ? label : params.value;
          } }
      ]
    },
    { headerName: "FP_GRADE", field: "grade", colId: "grade", cellClass: "stringType", width: 110, editable: editableWhenByproduct },
    // old는 dataType:"number"(RealGrid 기본 = 소수 그대로 표시)라 numberColumn(maximumFractionDigits:0)을
    // 쓰면 비율형 생산 값(0.5 등)이 정수로 반올림돼 보인다 → 소수 값 컬럼 선례(report/ItemCycleTime.js)와
    // 동일하게 decimalPointColumn을 쓰고 우측 정렬만 §3-1대로 얹는다.
    { headerName: "FP_OUTPUT_VAL", field: "outputVal", colId: "outputVal", width: 110,
      type: ["decimalPointColumn", "rightAligned"], editable: editableWhenByproduct },
    { headerName: "FP_OUTPUT_VAL_TYPE", field: "outputValTpCd", colId: "outputValTpCd", width: 110,
      type: ["centerAligned"], editable: editableWhenByproduct,
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_OUTPUT_VAL_TP_CD", outputValTpValuesRef, t) },
    // 수정 정보(audit) 그룹 — 접힘 대표 = 수정일시(old groupShowMode "always"), 나머지는 펼칠 때만(§0-5).
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    },
    // id는 axios-bigint의 useNativeBigInt로 파싱돼 typeof "bigint" — AG Grid 타입 추론을 끈다(Route.js와 동일).
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_BYPRODUCT", field: "byproductYn", colId: "byproductYn", hide: true, type: ["nonEditableColumn"] },
    { headerName: "type", field: "type", colId: "type", hide: true, type: ["nonEditableColumn"] }
  ];

  // old byproductGridItems의 첫 컬럼(routeCd) — RealGrid TreeGrid는 첫 컬럼에 계층을 그리고
  // displayCallback으로 행 종류별 표시값을 바꿨다. AG Grid는 그 자리가 autoGroupColumnDef다.
  // (autoGroupColumnDef는 GridUtils.setColumnDefs를 거치지 않으므로 헤더를 여기서 직접 t()로 번역한다 — §3 대안 패턴.)
  const autoGroupColumnDef = useMemo(() => ({
    headerName: t("FP_ROUTE_CD"),
    width: 280,
    editable: false,
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: (params) => {
        // old buttonVisibleCallback — 공정 코드 조회 버튼은 **신규 추가한 공정 행**에만 노출한다
        // (저장된 공정은 부산물의 소속을 바꾸지 못하게 확정).
        const pickable = params.data?.type === ROW_TYPE.ROUTE && params.data?.isNewRoute;
        // 아직 공정을 안 고른 신규 행은 old `placeHolder`처럼 흐린 안내문("공정 선택")으로 보여준다
        // (실제 값이 아니라 입력 안내라는 걸 구분해야 하므로 색만 다르게 준다).
        const isPlaceholder = params.data?.type === ROW_TYPE.ROUTE && !params.data?.routeCd;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
            <TreeItemIcon type={params.data?.type} />
            {pickable
              ? (
                <Box sx={{ flex: 1, minWidth: 0, ...(isPlaceholder ? { color: "text.disabled" } : {}) }}>
                  <CodePickerRenderer {...params} onOpenPopup={(node) => setRoutePopup({ open: true, node })} />
                </Box>
              )
              : <span>{params.value}</span>}
          </Box>
        );
      }
    },
    valueGetter: (params) => {
      const data = params.data;
      if (!data) return "";
      if (data.type === ROW_TYPE.BYPRODUCT) return t("FP_BYPRODUCT");
      if (data.type === ROW_TYPE.MAIN) return t("FP_MAIN_ITEM");
      return data.routeCd ? `${data.routeCd} (${data.routeNm ?? ""})` : t("FP_ROUTE_SELECT");
    }
  }), [t]);

  const retrieveByproducts = () => {
    if (!planScope) return;

    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    // planScope 단위로 전량 로드 → 공정/인벤토리 코드 필터는 AG Grid 외부 필터(클라)에서 처리(§6-1).
    fpByproductService.getAll({ viewId: viewName, subId: "", planScope })
      .then((response) => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;

        if (isEmpty(results)) {
          setRows([]);
          setSelectedRoute(null);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        setRows(flattenByproductTrees(results));
        // old loadData — 첫 행 기준으로 하단 다이어그램을 바로 조회한다.
        setSelectedRoute({ planScope: results[0].planScope ?? planScope, routeCd: results[0].routeCd });
      })
      .catch((e) => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        const reduxState = reduxUtil.createObjectState(viewName, { planScope, ...searchFields });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
        gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  // 하단 다이어그램 — 그리드가 아니라 ReactFlow라 그리드 로딩 오버레이를 쓸 수 없고(§14-4),
  // 행을 클릭할 때마다 전체화면 Backdrop을 띄우면 조작을 막으므로 별도 표시 없이 조회한다
  // (old도 두 조회 모두 waitOn:false로 스피너를 띄우지 않았다).
  const retrieveRouteFlo = (params) => {
    if (!params?.planScope || !params?.routeCd) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const reqId = ++latestDiagramReqRef.current;

    fpByproductService.getRouteFlo({ viewId: viewName, subId: "", planScope: params.planScope, routeCd: params.routeCd })
      .then((response) => {
        if (reqId !== latestDiagramReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setNodes([]);
          setEdges([]);
          return;
        }
        const flow = byproductFloToFlow(results[0], t);
        setNodes(flow.nodes);
        setEdges(flow.edges);
      })
      .catch((e) => {
        debug(e);
        if (reqId !== latestDiagramReqRef.current) return;
        setNodes([]);
        setEdges([]);
      });
  };

  const onGridReady = (params) => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  // old onCellClicked — 클릭한 행이 속한 공정으로 하단 다이어그램을 갱신한다(자식 행도 routeCd를 갖는다).
  const onCellClicked = (e) => {
    const routeCd = e.data?.routeCd;
    if (!routeCd) {
      setSelectedRoute(null);
      return;
    }
    if (routeCd === selectedRoute?.routeCd) return;
    setSelectedRoute({ planScope: e.data.planScope ?? planScope, routeCd });
  };

  const addStateRow = (api, row, addIndex = 0) => {
    const node = GridUtils.addRow(api, row, addIndex, false);
    DataState.setDataState(node);
    return node;
  };

  const newByproductRow = (routeKey, routeCd) => ({
    id: null, planScope, routeCd, inventoryCd: "", grade: "", outputVal: null, outputValTpCd: null,
    byproductYn: true, type: ROW_TYPE.BYPRODUCT, path: [routeKey, `byproduct__new${++newRowSeqRef.current}`]
  });

  // old GridAddRowButton + dataSource.onRowAdded — 공정 행을 추가하면 그 아래에
  // [주품목 1행 + 부산물 1행]이 자동으로 같이 생긴다.
  const insertRoute = () => {
    const api = gridRef.current.api;
    api.stopEditing();

    const routeKey = `NEW__${++newRowSeqRef.current}`;
    // ⚠️ 자식 표시 순서 = **데이터 배열에 들어간 순서**다(트리는 같은 부모 안에서 데이터 순서를 지킨다).
    // 세 행을 전부 addIndex 0으로 넣으면 매번 맨 앞에 꽂혀 [연산품 → 주품목]으로 뒤집히므로
    // old 순서(공정 → 주품목 → 연산품) 그대로 0/1/2를 명시한다.
    const routeNode = addStateRow(api, { id: null, planScope, routeCd: "", routeNm: "", byproductYn: false, type: ROW_TYPE.ROUTE, isNewRoute: true, path: [routeKey] }, 0);
    addStateRow(api, { id: null, planScope, routeCd: "", byproductYn: false, type: ROW_TYPE.MAIN, path: [routeKey, "main"] }, 1);
    addStateRow(api, newByproductRow(routeKey, ""), 2);

    // 조회 직후의 전체 전개(groupDefaultExpanded)는 트랜잭션으로 새로 만들어진 그룹 행에는 적용되지
    // 않는다 — 공정 행만 보이고 [주품목/연산품] 두 행이 접힌 채 숨는 것을 막으려고 명시적으로 편다.
    api.getRowNode(routeNode.id)?.setExpanded(true);
  };

  // old 그리드 컨텍스트 메뉴 "공정 하위 연산품 추가"(FP_ADD_BYPRODUCT) 대응 — 우클릭 메뉴 대신
  // 조회조건 영역의 버튼으로 이관한다(컨텍스트 메뉴 자체는 Tier2, 기능은 Tier1이라 유지).
  // TODO(MIG-T2): [byproduct/Byproduct] 그리드 우클릭 컨텍스트 메뉴 UI 미이관.
  //   old: gridView.setContextMenu([{label: FP_ADD_BYPRODUCT}]) + onContextMenuItemClicked에서 부산물 자식 행 추가.
  //   사유: 컨텍스트 메뉴는 Tier2(saas-fp/docs/fp-frontend-migration/CLAUDE.md) — 동일 기능을 상단 버튼으로 제공해 기능 손실은 없다.
  const insertByproduct = () => {
    const api = gridRef.current.api;
    api.stopEditing();

    const focused = api.getFocusedCell();
    const node = focused ? api.getDisplayedRowAtIndex(focused.rowIndex) : null;
    if (!node?.data) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoSelectedRow") });
      return;
    }

    const routeNode = node.data.type === ROW_TYPE.ROUTE ? node : node.parent;
    const routeKey = routeNode?.data?.path?.[0];
    if (!routeKey) return;

    // 데이터 배열 끝에 넣어 그 공정의 **마지막 자식**이 되게 한다(주품목 위로 끼어들지 않게 —
    // addIndex 0으로 넣으면 형제 중 맨 앞이 된다). 범위를 넘는 addIndex는 AG Grid가 끝으로 클램프한다.
    addStateRow(api, newByproductRow(routeKey, routeNode.data.routeCd), api.getDisplayedRowCount() + 1);
    routeNode.setExpanded(true);
  };

  const removeByproducts = () => {
    const api = gridRef.current.api;
    api.stopEditing();

    // old preDeleteData — 선택 행 중 **부산물 행만** 삭제 대상이다(주품목/공정 행은 실체가 없다).
    const selected = api.getSelectedRows().filter((row) => row.type === ROW_TYPE.BYPRODUCT);
    const targets = selected.filter((row) => row.id);
    // 미저장 신규 행은 서버에 없으므로 그리드에서 바로 제거한다(old GridDeleteRowButton의 newRows 인자 대응).
    const newRows = selected.filter((row) => !row.id);

    if (newRows.length > 0) {
      api.applyTransaction({ remove: newRows });
    }

    if (targets.length === 0) {
      if (newRows.length === 0) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      }
      return;
    }

    fpByproductService.remove(targets.map(stripClientFields))
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
      })
      .catch((e) => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
      })
      .then(() => {
        retrieveByproducts();
      });
  };

  const saveByproducts = () => {
    const api = gridRef.current.api;
    api.stopEditing();

    // old preSaveData — 변경분 중 **부산물 행만** 저장한다(공정/주품목 행은 화면 표시용 가상 행).
    const changes = DataState.getAllStateData(api).filter((row) => row.type === ROW_TYPE.BYPRODUCT);

    if (changes.length === 0) {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 필수값 검사도 부산물 행만(§9-2 + old onValidateColumn의 type 분기).
    const missingRequired = findMissingRequired(api, REQUIRED_FIELDS)
      .filter((missing) => api.getDisplayedRowAtIndex(missing.rowIndex)?.data?.type === ROW_TYPE.BYPRODUCT);

    if (missingRequired.length > 0) {
      setProgressOn(false);
      const labels = [...new Set(missingRequired.map((missing) => t(missing.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(api, missingRequired[0]);
      return;
    }

    fpByproductService.upsert(changes.map(stripClientFields))
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch((e) => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
      })
      .then(() => {
        setProgressOn(false);
        retrieveByproducts();
      });
  };

  // old setRouteValues — 공정을 고르면 공정 행과 자식 행 전체에 공정 코드를 반영하고,
  // 주품목 행에는 그 공정이 생산하는 인벤토리/품목 정보까지 채운다.
  //
  // ⚠️ `node.setDataValue("routeCd", …)`를 쓰면 안 된다 — 공정 코드는 트리 표시 컬럼
  // (`autoGroupColumnDef`)으로 옮겨가서 `routeCd`라는 **컬럼 자체가 없기 때문에** 값이 반영되지
  // 않는다(화면에도 안 보이고, 저장 payload의 routeCd도 비어 나간다). 행 데이터를 통째로 바꾸는
  // `updateData`를 쓰고, 값이 파생되는 트리 컬럼(valueGetter)까지 확실히 다시 그리도록 refreshCells를 붙인다.
  const onSelectRoute = (row) => {
    const node = routePopup.node;
    if (!node) return;

    const api = gridRef.current?.api;
    node.updateData({ ...node.data, routeCd: row.routeCd, routeNm: row.routeNm });

    const touched = [node];
    (node.childrenAfterGroup ?? []).forEach((child) => {
      const updated = { ...child.data, routeCd: row.routeCd };
      if (child.data?.type === ROW_TYPE.MAIN) {
        updated.routeNm = row.routeNm;
        updated.inventoryCd = row.inventoryCd;
        updated.inventoryNm = row.inventoryNm;
        updated.itemCd = row.itemCd;
        updated.itemNm = row.itemNm;
        updated.itemClassCd = row.itemClassCd;
      }
      child.updateData(updated);
      touched.push(child);
    });

    api?.refreshCells({ rowNodes: touched, force: true });
    setSelectedRoute({ planScope, routeCd: row.routeCd });
  };

  // old setInventoryValues — 부산물 행의 인벤토리/품목 정보를 채운다.
  const onSelectInventory = (row) => {
    const node = inventoryPopup.node;
    if (!node) return;

    node.setDataValue("inventoryCd", row.inventoryCd);
    node.setDataValue("inventoryNm", row.inventoryNm);
    node.setDataValue("itemCd", row.itemCd);
    node.setDataValue("itemNm", row.itemNm);
    node.setDataValue("itemClassCd", row.itemClassCd);
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setProgressOn(true);
      saveByproducts();
    } else if (action === "DELETE") {
      removeByproducts();
    }
  };

  // 서버 조회는 planScope(스코프 파티션) 변경 시에만(§6). 코드/명칭 검색은 클라 외부 필터다.
  useEffect(() => {
    if (!gridLoading) return;
    retrieveByproducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, planScope]);

  useEffect(() => {
    retrieveRouteFlo(selectedRoute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute]);

  // 노드 더블클릭 → 해당 기준정보 화면으로 딥링크 이동(§13-1).
  const onNodeDoubleClick = (event, node) => {
    const nav = node?.data?.nav;
    if (!nav?.reduxKey) return;
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(nav.reduxKey, nav.fields)));
    reduxDispatch(reduxUtil.updateHistoryState(nav.reduxKey));
  };

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={SEARCH_FIELD_KEYS}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
        />
        <Box sx={{ display: "flex" }}>
          <Button size="small" onClick={insertByproduct}>{t("FP_ADD_BYPRODUCT")}</Button>
          <AddButton onClick={insertRoute} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </FilterContainer>
      {/* old ResultArea sizes={[50, 50]} direction="vertical" — 상단 트리 그리드 / 하단 다이어그램 1:1 분할
          (SplitterContainer 기본값 [3,2]는 60/40이라 명시적으로 1:1을 준다 — report/OrderTracking.js와 동일). */}
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-byproduct-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rows}
            autoGroupColumnDef={autoGroupColumnDef}
            {...defaultGridMemo}
            rowSelection={{
              ...defaultGridMemo.rowSelection,
              headerCheckbox: true,
              checkboxes: true,
              // old checkableCallback — 주품목 행은 체크 대상이 아니다(삭제/저장 대상이 아님).
              isRowSelectable: (node) => node.data?.type !== ROW_TYPE.MAIN,
              hideDisabledCheckboxes: true,
              // old onItemChecked — 공정 행을 체크하면 하위 부산물이 같이 체크된다.
              groupSelects: "descendants"
            }}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            onCellClicked={onCellClicked}
            onGridReady={onGridReady}
          />
        </Box>
        <Box id="fp-byproduct-diagram" className="grid-area" sx={{ height: "calc(100% - 8px)", mt: 1, minHeight: 0 }}>
          <FlowChart nodes={nodes} edges={edges} onNodeDoubleClick={onNodeDoubleClick} />
        </Box>
      </SplitterContainer>
      <RoutePopup
        open={routePopup.open}
        onClose={() => setRoutePopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectRoute}
      />
      <InventoryPopup
        open={inventoryPopup.open}
        onClose={() => setInventoryPopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectInventory}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(Byproduct);
